import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';
import ActionsLogsDialog from '../ActionsLogsDialog';

const GlobalSettingActionsHistory = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { ws } = useWebSocket();
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Filter states for the dialog
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedTimeventId, setSelectedTimeventId] = useState('');
  const [maxLogs, setMaxLogs] = useState(100);
  
  // Available cases for dropdown
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    refreshData: () => {
      // Refresh cases when parent requests
      fetchCases();
    },
    isModified: () => false,
    saveChanges: () => Promise.resolve(true)
  }));

  // Fetch cases for dropdown
  const fetchCases = React.useCallback(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      setLoadingCases(true);
      ws.send(JSON.stringify({ command: 'getcases' }));
    }
  }, [ws]);

  // Listen for WebSocket messages to get cases
  React.useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.command === 'listcases') {
          console.log('GlobalSettingActionsHistory: Received cases response:', message);
          setLoadingCases(false);
          if (message.cases && Array.isArray(message.cases)) {
            setCases(message.cases);
          }
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws]);

  // Fetch cases on component mount
  React.useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Handle case selection change
  const handleCaseChange = (event) => {
    setSelectedCaseId(event.target.value);
    // Clear TimeEvent when case changes
    if (event.target.value) {
      setSelectedTimeventId('');
    }
  };

  // Handle TimeEvent ID input change
  const handleTimeventIdChange = (event) => {
    setSelectedTimeventId(event.target.value);
  };

  // Handle max logs change
  const handleMaxLogsChange = (event) => {
    setMaxLogs(parseInt(event.target.value) || 100);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedCaseId('');
    setSelectedTimeventId('');
    setMaxLogs(100);
  };

  // Open the actions logs dialog with current filters
  const handleViewActions = () => {
    setDialogOpen(true);
  };

  // Close the dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#5757b0' }}>
            {t('global_setting.actions_history_section')}
          </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('global_setting.actions_history_description')}
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon />
          {t('global_setting.filters_title', 'Filter Options')}
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Project Selection */}
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('global_setting.filter_project_label', 'Project')}</InputLabel>
              <Select
                value={selectedCaseId}
                label={t('global_setting.filter_project_label')}
                onChange={handleCaseChange}
                disabled={loadingCases}
              >
                <MenuItem value="">
                  <em>{t('sidebar.all_projects_chip', 'All projects')}</em>
                </MenuItem>
                {cases.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={caseItem.id}>
                    {caseItem.title || t('global_setting.project_title_with_id', 'Project {{id}}', { id: caseItem.id })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* TimeEvent ID */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('global_setting.filter_timeevent_label', 'TimeEvent ID')}
              value={selectedTimeventId}
              onChange={handleTimeventIdChange}
              placeholder={t('global_setting.filter_timeevent_placeholder', 'Enter TimeEvent ID (optional)')}
              helperText={t('global_setting.filter_timeevent_helper', 'Leave empty to show all timeevents')}
            />
          </Grid>

          {/* Max Results */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('global_setting.filter_max_results_label', 'Max Results')}
              value={maxLogs}
              onChange={handleMaxLogsChange}
              inputProps={{ min: 10, max: 500 }}
              helperText={t('global_setting.filter_max_results_helper', '10-500 results')}
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<ViewIcon />}
            onClick={handleViewActions}
            sx={{ backgroundColor: '#5757b0', '&:hover': { backgroundColor: '#7777d0' } }}
          >
            {(!selectedCaseId && !selectedTimeventId) ? t('global_setting.view_all_actions', 'View All Actions') : t('global_setting.view_filtered_actions', 'View Filtered Actions')}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            {t('global_setting.clear_filters', 'Clear Filters')}
          </Button>
        </Box>

        {/* Current Filter Summary */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {selectedCaseId && (
            <Alert severity="info" sx={{ py: 0, px: 1 }}>
              <Typography variant="caption">
                {t('global_setting.current_filter_project_label', 'Project')}: {cases.find(c => c.id === selectedCaseId)?.title || selectedCaseId}
              </Typography>
            </Alert>
          )}
          {selectedTimeventId && (
            <Alert severity="info" sx={{ py: 0, px: 1 }}>
                <Typography variant="caption">
                  {t('global_setting.current_filter_timevent_label', 'TimeEvent')}: {selectedTimeventId}
              </Typography>
            </Alert>
          )}
          <Alert severity="info" sx={{ py: 0, px: 1 }}>
            <Typography variant="caption">
              {t('global_setting.current_filter_max_results_label', 'Max Results')}: {maxLogs}
            </Typography>
          </Alert>
        </Box>
      </Paper>

      {/* Instructions */}
      <Paper sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#5757b0' }}>
          {t('global_setting.actions_history_howto_title', 'How to Use Actions History')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('global_setting.actions_history_intro')}
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>{t('global_setting.howto_all_actions_label', 'All Actions:')}</strong> {t('global_setting.howto_all_actions_desc', 'Leave filters empty and click "View All Actions" to see recent actions across all projects')}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>{t('global_setting.howto_project_label', 'Project-Specific:')}</strong> {t('global_setting.howto_project_desc', 'Select a project to see only actions related to that project')}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            <strong>{t('global_setting.howto_timevent_label', 'TimeEvent-Specific:')}</strong> {t('global_setting.howto_timevent_desc', 'Enter a TimeEvent ID to see actions for a specific time event')}
          </Typography>
          <Typography component="li" variant="body2">
            <strong>{t('global_setting.howto_advanced_label', 'Advanced Filtering:')}</strong> {t('global_setting.howto_advanced_desc', 'The dialog provides additional filters for status, action type, and execution context')}
            </Typography>
        </Box>
      </Paper>

      {/* Actions Logs Dialog */}
      <ActionsLogsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={t('global_setting.actions_history_dialog_title', 'Actions History - Global View')}
        caseid={selectedCaseId || null}
        timeevent_id={selectedTimeventId || null}
        maxLogs={maxLogs}
        showFilters={true}
        maxHeight="80vh"
        defaultShowAll={true}
      />
    </Box>
  );
});

GlobalSettingActionsHistory.displayName = 'GlobalSettingActionsHistory';

export default GlobalSettingActionsHistory;